import { defineComponent, h, onMounted, ref, resolveComponent } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue'

import nav from '@/utils/navigators'

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)
  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}

const getSelectedCountry = () => localStorage.getItem('connection') || 'spain'

const getSwaggerDocumentationUrl = () => {
  if ('romania' === getSelectedCountry()) {
    return `${process.env.VUE_APP_API_BASE_URL}/api/documentation?urls.primaryName=Romania+Partner+API#/Send%20lead%20request`
  }

  return `${process.env.VUE_APP_API_BASE_URL}/api/documentation#/Send%20lead%20request`
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',

  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },

  setup() {
    const route = useRoute()
    const firstRender = ref(true)

    onMounted(() => {
      firstRender.value = false
    })

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      if (item.to == '/latest-documentation') {
        return h(
          RouterLink,
          {
            to: item.to,
            custom: true,
          },
          {
            default: (props) =>
              h(
                resolveComponent(item.component),
                {
                  active: props.isActive,
                  href: 'javascript:void(0);',
                  class: 'nav-link-documentation',
                  onClick: () =>
                    window.open(getSwaggerDocumentationUrl(), '_blank'),
                },
                {
                  default: () => [
                    item.icon &&
                      h(resolveComponent('CIcon'), {
                        customClassName: 'nav-icon',
                        name: item.icon,
                      }),
                    item.name,
                    h(resolveComponent('CIcon'), {
                      class: 'svg-extra',
                      name: 'cil-arrow-thick-from-left',
                    }),
                  ],
                },
              ),
          },
        )
      }

      if (item.to == '/documentation') {
        return h(
          RouterLink,
          {
            to: item.to,
            custom: true,
          },
          {
            default: (props) =>
              h(
                resolveComponent(item.component),
                {
                  active: props.isActive,
                  href: 'javascript:void(0);',
                  class: 'nav-link-documentation',
                  onClick: () => window.open('/documentation', '_blank'),
                },
                {
                  default: () => [
                    item.icon &&
                      h(resolveComponent('CIcon'), {
                        customClassName: 'nav-icon',
                        name: item.icon,
                      }),
                    item.name,
                    h(resolveComponent('CIcon'), {
                      class: 'svg-extra',
                      name: 'cil-arrow-thick-from-left',
                    }),
                  ],
                },
              ),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon &&
                        h(resolveComponent('CIcon'), {
                          customClassName: 'nav-icon',
                          name: item.icon,
                        }),
                      // Wrapped so the label keeps a gap before the badge.
                      item.badge
                        ? h('span', { class: 'nav-link-text' }, item.name)
                        : item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => nav.map((item) => renderItem(item)),
        },
      )
  },
})
export { AppSidebarNav }
